// MATLAB language definition for Monaco Editor using Monarch tokenizer
export const matlabLanguageDefinition = {
  // Set defaultToken to empty - don't mark unknown tokens as invalid
  defaultToken: '',

  keywords: [
    'if', 'else', 'elseif', 'end', 'for', 'while', 'do', 'break', 'continue',
    'function', 'return', 'switch', 'case', 'otherwise', 'try', 'catch',
    'classdef', 'methods', 'properties', 'events', 'enumeration', 'arguments',
    'global', 'persistent', 'clear', 'clc', 'close', 'exit', 'quit',
    'true', 'false', 'pi', 'eps', 'inf', 'nan', 'i', 'j'
  ],

  builtins: [
    // Math functions
    'abs', 'acos', 'acosh', 'asin', 'asinh', 'atan', 'atan2', 'atanh',
    'ceil', 'cos', 'cosh', 'exp', 'fix', 'floor', 'log', 'log10', 'log2',
    'max', 'min', 'mod', 'rem', 'round', 'sign', 'sin', 'sinh', 'sqrt',
    'tan', 'tanh',
    // Array functions
    'length', 'size', 'numel', 'ndims', 'find', 'sort', 'unique', 'sum',
    'mean', 'std', 'var', 'median', 'mode', 'range',
    // Matrix functions
    'zeros', 'ones', 'eye', 'rand', 'randn', 'randi', 'linspace', 'logspace',
    'meshgrid', 'transpose', 'inv', 'det', 'trace', 'rank', 'norm',
    // I/O functions
    'disp', 'fprintf', 'sprintf', 'input', 'load', 'save', 'imread', 'imwrite',
    // Plot functions
    'plot', 'subplot', 'figure', 'hold', 'grid', 'xlabel', 'ylabel', 'title',
    'legend', 'axis', 'xlim', 'ylim', 'surf', 'mesh', 'contour', 'histogram',
    // String functions
    'char', 'string', 'strcat', 'strcmp', 'strcmpi', 'strfind', 'strrep',
    'split', 'join', 'contains', 'startsWith', 'endsWith'
  ],

  operators: [
    '=', '==', '~=', '<', '<=', '>', '>=',
    '+', '-', '*', '/', '\\', '^', '.*', './', '.\\', '.^',
    '&', '|', '~', '&&', '||',
    ':', ';', ',', '(', ')', '[', ']', '{', '}', '.'
  ],

  // Common regular expressions
  symbols: /[=><!~?:&|+\-*/\\^%;.,()[\]{}]/,

  // The main tokenizer for MATLAB
  tokenizer: {
    root: [
      // Comments
      [/%.*$/, 'comment'],

      // Block comments (not standard in MATLAB but sometimes used)
      [/%\{/, 'comment', '@comment'],

      // Identifiers and keywords
      [/[a-zA-Z_]\w*/, {
        cases: {
          '@keywords': 'keyword',
          '@builtins': 'type.identifier',
          '@default': 'identifier'
        }
      }],

      // Numbers
      [/\d*\.\d+([eE][+-]?\d+)?/, 'number.float'],
      [/\d+[eE][+-]?\d+/, 'number.float'],
      [/\d+/, 'number'],

      // Complex numbers
      [/\d*\.?\d+[ij]/, 'number.float'],

      // Strings
      [/'([^'\\]|\\.)*'/, 'string'],
      [/"([^"\\]|\\.)*"/, 'string'],

      // Delimiters and operators
      [/[{}()[\]]/, '@brackets'],
      [/[<>]=?/, 'operator'],
      [/[=~!]=/, 'operator'],
      [/[+\-*/\\^]/, 'operator'],
      [/[&|~]/, 'operator'],
      [/[;,.]/, 'delimiter'],
      [/:/, 'operator'],

      // Whitespace
      { include: '@whitespace' }
    ],

    comment: [
      [/[^%}]+/, 'comment'],
      [/%}/, 'comment', '@pop'],
      [/%/, 'comment']
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white']
    ]
  }
};

export const matlabLanguageConfiguration = {
  comments: {
    lineComment: '%',
    blockComment: ['%{', '%}']
  },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')']
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"', notIn: ['string'] },
    { open: "'", close: "'", notIn: ['string', 'comment'] }
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" }
  ]
};

// MATLAB completion provider
export const matlabCompletionProvider = {
  provideCompletionItems: (model, position, monaco) => {
    const word = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn,
    };

    const suggestions = [];

    // Add keyword suggestions
    matlabLanguageDefinition.keywords.forEach(keyword => {
      if (keyword.startsWith(word.word) && word.word.length > 0) {
        suggestions.push({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: keyword,
          range: range,
        });
      }
    });

    // Add builtin function suggestions
    matlabLanguageDefinition.builtins.forEach(builtin => {
      if (builtin.startsWith(word.word) && word.word.length > 0) {
        suggestions.push({
          label: builtin,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: builtin + '()',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: range,
          detail: 'MATLAB built-in function'
        });
      }
    });

    // Function template snippets
    if (word.word === 'func' || word.word === 'function') {
      suggestions.push({
        label: 'function',
        kind: monaco.languages.CompletionItemKind.Snippet,
        // eslint-disable-next-line no-template-curly-in-string
        insertText: 'function ${2:output} = ${1:functionName}(${3:input})\n    ${4:% Function implementation}\n    ${2:output} = ${5:result};\nend',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: range,
        documentation: 'MATLAB function template',
      });
    }

    // For loop template
    if (word.word === 'for') {
      suggestions.push({
        label: 'for loop',
        kind: monaco.languages.CompletionItemKind.Snippet,
        // eslint-disable-next-line no-template-curly-in-string
        insertText: 'for ${1:i} = ${2:1:n}\n    ${3:% loop body}\nend',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: range,
        documentation: 'MATLAB for loop template',
      });
    }

    // While loop template
    if (word.word === 'while') {
      suggestions.push({
        label: 'while loop',
        kind: monaco.languages.CompletionItemKind.Snippet,
        // eslint-disable-next-line no-template-curly-in-string
        insertText: 'while ${1:condition}\n    ${2:% loop body}\nend',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: range,
        documentation: 'MATLAB while loop template',
      });
    }

    // If statement template
    if (word.word === 'if') {
      suggestions.push({
        label: 'if statement',
        kind: monaco.languages.CompletionItemKind.Snippet,
        // eslint-disable-next-line no-template-curly-in-string
        insertText: 'if ${1:condition}\n    ${2:% if body}\nend',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range: range,
        documentation: 'MATLAB if statement template',
      });
    }

    return { suggestions };
  }
};